import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useProducer } from '../../common/hooks/useProducer';
import { ordersAPI } from '../../services/api';
import Button from '../../common/components/Button';
import Card from '../../common/components/Card';
import Loading from '../../common/components/Loading';
import Table from '../../common/components/Table';
import { Plus, Eye, Trash2, Package, CheckCircle } from 'lucide-react';
import { formatCurrency, formatDate } from '../../common/utils/formatters';
import toast from 'react-hot-toast';
import './OrdersScreen.css';

const OrdersScreen = () => {
  const navigate = useNavigate();
  const { profile } = useProducer();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('all'); // all, pending, fulfilled

  const { data: orders, isLoading } = useQuery('orders', async () => {
    const response = await ordersAPI.list();
    return response.data.data;
  });

  const deleteMutation = useMutation(
    (id) => ordersAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('orders');
        toast.success('Order deleted successfully');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to delete order');
      },
    }
  );

  const fulfillMutation = useMutation(
    (id) => ordersAPI.fulfill(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('orders');
        queryClient.invalidateQueries('prices'); // Update stock counts
        toast.success('Order fulfilled successfully');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to fulfill order');
      },
    }
  );

  const handleView = (order) => {
    navigate(`/${profile.url_friendly_name}/orders/${order.id}`);
  };

  const handleDelete = (order) => {
    if (window.confirm(`Are you sure you want to delete this order for ${order.customer_name}?`)) {
      deleteMutation.mutate(order.id);
    }
  };

  const handleFulfill = (order) => {
    if (window.confirm(`Are you sure you want to fulfill this order? This will deduct items from stock.`)) {
      fulfillMutation.mutate(order.id);
    }
  };

  if (isLoading) {
    return <Loading message="Loading orders..." />;
  }

  const ordersList = Array.isArray(orders) ? orders : [];

  // Filter orders
  const filteredOrders = ordersList.filter(order => {
    if (!order) return false;
    if (filter === 'pending') return !order.is_fulfilled;
    if (filter === 'fulfilled') return order.is_fulfilled;
    return true;
  });

  const columns = [
    {
      header: 'Order Date',
      accessor: 'created_at',
      render: (row) => formatDate(row.created_at),
    },
    {
      header: 'Customer',
      accessor: 'customer_name',
    },
    {
      header: 'Source',
      accessor: 'order_source',
      render: (row) => {
        const value = row.order_source;
        const source = value ? String(value) : 'Manual';
        return (
          <span className={`order-source-badge ${source.toLowerCase()}`}>
            {source}
          </span>
        );
      },
    },
    {
      header: 'Items',
      accessor: 'item_count',
    },
    {
      header: 'Total',
      accessor: 'total_amount',
      render: (row) => formatCurrency(row.total_amount || 0),
    },
    {
      header: 'Status',
      accessor: 'is_fulfilled',
      render: (row) => (
        <span className={`order-status-badge ${row.is_fulfilled ? 'fulfilled' : 'pending'}`}>
          {row.is_fulfilled ? 'Fulfilled' : 'Pending'}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (row) => (
        <div className="order-actions">
          <button
            className="action-button view"
            onClick={(e) => {
              e.stopPropagation();
              handleView(row);
            }}
            title="View Details"
          >
            <Eye size={16} />
          </button>
          {row && !row.is_fulfilled && (
            <>
              <button
                className="action-button fulfill"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFulfill(row);
                }}
                title="Fulfill Order"
              >
                <CheckCircle size={16} />
              </button>
              <button
                className="action-button delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row);
                }}
                title="Delete Order"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const stats = {
    total: ordersList.length,
    pending: ordersList.filter(o => o && !o.is_fulfilled).length,
    fulfilled: ordersList.filter(o => o && o.is_fulfilled).length,
  };

  return (
    <div className="orders-screen">
      <div className="orders-header">
        <div className="header-content">
          <h1>
            <Package size={32} />
            Orders
          </h1>
          <p>Manage customer orders and fulfillment</p>
        </div>
        <Button
          variant="primary"
          icon={<Plus size={20} />}
          onClick={() => navigate(`/${profile.url_friendly_name}/orders/add`)}
        >
          Add Order
        </Button>
      </div>

      <div className="orders-stats">
        <Card className="stat-card">
          <div className="stat-label">Total Orders</div>
          <div className="stat-value">{stats.total}</div>
        </Card>
        <Card className="stat-card">
          <div className="stat-label">Pending</div>
          <div className="stat-value pending">{stats.pending}</div>
        </Card>
        <Card className="stat-card">
          <div className="stat-label">Fulfilled</div>
          <div className="stat-value fulfilled">{stats.fulfilled}</div>
        </Card>
      </div>

      <Card>
        <div className="orders-filters">
          <button
            className={`filter-button ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Orders
          </button>
          <button
            className={`filter-button ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            className={`filter-button ${filter === 'fulfilled' ? 'active' : ''}`}
            onClick={() => setFilter('fulfilled')}
          >
            Fulfilled
          </button>
        </div>

        {filteredOrders.length > 0 ? (
          <Table columns={columns} data={filteredOrders} />
        ) : (
          <div className="no-orders">
            <Package size={48} />
            <p>No orders found</p>
            <Button onClick={() => navigate(`/${profile.url_friendly_name}/orders/add`)}>
              Add Your First Order
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default OrdersScreen;
